// @ts-strict-ignore
import { ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { lastValueFrom, Subject } from 'rxjs';
import { AddArticlePresenter } from '../presenter/add-article.presenter';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';
import { ArticleUtils } from '@article/util/article-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArrayUtils } from '@shared/util/array-utils';
import { AccessoryTypeFilter } from '../model/accessory-type-filter';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { UserModel } from '@user/model/user.model';
import { ArticleCollectionVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { SelectionModel } from '../model/selection.model';
import { ProjectModel } from '../../../model/project.model';
import { SelectionMetaElementUsageIdentity } from '@shared/vo/meta-element.vo';
import { SelectionArticleGroupIdentity } from '../model/vo/selection-article-group-identity';
import { UserActionsLoggingManager } from '@user/service/logging/user-actions-logging.manager';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { AccessoryEditorAction, AccessoryEditorStateManager } from './service/accessory-editor-state.manger';
import { CollectionService } from '@user/components/favorites/service/collection.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { ProjectChanged, ProjectChangedType } from '../../../model/project-changed';
import { LabelEditorStateManager } from '../../label/label-editor/service/label-editor-state.manager';
import { ProjectValidationService } from '../../validation/service/project-validation.service';
import { ArticleTechnicalInfoService } from '@article/service/article-technical-info.service';
import { SuitableConnectionsSetVO } from '../model/vo/suitable-connections-set.vo';
import { SuitableConnectionsVO } from '../model/vo/suitable-connections.vo';
import { ArticleCreationService } from '../service/article-creation.service';
import { Log } from '@shared/debug/log';
import { DefaultReplaceArticleManager } from '../manager/default-replace-article.manager';
import { AlternativeReplaceArticleManager } from '../manager/alternative-replace-article.manager';
import { mergeMap } from 'rxjs/operators';
import { SelectionModelService } from '../service/selection-model.service';
import { AddArticlePresenterFactory } from '../presenter/add-article-presenter.factory';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { SearchQueryType } from '../model/vo/search-query-type';
import { ArticleInfoConnectionSetVO } from '../model/vo/accessory-search-result.vo';
import { AccessoryService, AccessoryViewData } from '@article/service/accessory.service';
import { ArticleCreationManager } from '../manager/article-creation.manager';
import { ArticleCreationManagerFactory } from '../manager/article-creation.manager.factory';
import { WireBridgeService } from '../manager/wire-bridge.service';
import { ArticleWithTechnicalPropertiesDTO } from '@article/properties/technical/technical-requirements';
import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';

export class Accessory {
  constructor(
    public article: ArticleWithTechnicalPropertiesDTO,
    public articleNr: string,
    public image: string,
    public description: string,
    public outdatedArticleLabel: string,
    public color: string,
    public suitableConnections: SuitableConnectionsSetVO[],
    public customArticleNo?: string
  ) {}
}

export enum AccessoryEditorState {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
}

export enum AccessoryEditorMode {
  ACCESSORY = 'ACCESSORY',
  ALTERNATIVE = 'ALTERNATIVE',
}

/**
 * Overview of the available accessories for an article
 */
@Component({
  standalone: false,
  selector: 'app-accessory',
  templateUrl: './accessory.component.html',
  styleUrls: ['./accessory.component.scss'],
})
export class AccessoryComponent implements OnInit, OnDestroy {
  @HostBinding(`style.min-height.px`) minHeight = 330;

  @Input() public totalHeight: number;
  @Input() public articleTypeSelectionAllowed: boolean = true;
  @Input() public allowedAccessoryTypeKeys: string[] = null;

  @Output() public opened = new EventEmitter<void>();

  public accessoryList: Accessory[] = [];
  public accessoryCategories: string[] = [];
  public isOpen: boolean = false;
  public accSelectedRow: string = '';
  public onClose: Subject<boolean>;
  public notPrintableAvailable: boolean = false;
  private articleCreationManager: ArticleCreationManager;
  public addArticlePresenter: AddArticlePresenter;
  public selectedAccessory: Accessory;
  public noSearchResults: boolean = false;
  public nothingSelected: boolean = false;
  public isAlternativeSearch: boolean = false;

  private subscriptions: Subscription;

  constructor(
    private cdRef: ChangeDetectorRef,
    private userActionsLoggingManager: UserActionsLoggingManager,
    private userModel: UserModel,
    private alertHelper: AlertHelper,
    private articleManager: ArticleManager,
    private overlayService: OverlayService,
    private selectionModel: SelectionModel,
    private selectionModelService: SelectionModelService,
    private projectModel: ProjectModel,
    private collectionService: CollectionService,
    private sceneManager: SceneManager,
    private accessoryEditorStateManager: AccessoryEditorStateManager,
    private localeResourceManager: LocaleResourceManager,
    private _alternativeReplaceArticleManager: AlternativeReplaceArticleManager,
    private _defaultReplaceArticleManager: DefaultReplaceArticleManager,
    public labelEditorStateManager: LabelEditorStateManager,
    private projectValidationService: ProjectValidationService,
    private articleTechnicalInfoService: ArticleTechnicalInfoService,
    private _articleCreationService: ArticleCreationService,
    private articleUsageService: ArticleUsageService,
    private addArticlePresenterFactory: AddArticlePresenterFactory,
    private accessoryService: AccessoryService,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private wireBridgeService: WireBridgeService,
  ) {
  }

  public ngOnInit(): void {
    this.subscriptions = new Subscription();
    this.recreateArticlePresenter();
    this.subscriptions.add(this.accessoryService.accessoryViewDataChanged$.subscribe((data: AccessoryViewData) => this.setDataToConnectArticle(data)));
    this.subscriptions.add(this.projectModel.projectChanged$.subscribe((projectChanged) => this.projectChanged(projectChanged)));
    this.subscriptions.add(this.localeResourceManager.localeChanged$.subscribe(() => this.updateCategoriesLanguage()));
    this.subscriptions.add(this.accessoryEditorStateManager.actionAccessoriesEditor$.subscribe(
      (actionData: AccessoryEditorAction) => this.onActionAccessoriesEditor(actionData)
    ));
    this.subscriptions.add(this.projectValidationService.validateProjectView$.subscribe(() => this.closeAccessory()));
    this.subscriptions.add(this.articleTechnicalInfoService.articleTechnicalInfo$.subscribe(() => this.technicalInfosLoaded()));

    this.subscriptions.add(this._articleCreationService.articlesAdded$.subscribe({
      next: (data) => {
        if (data.type !== 'articlesAdded') {
          return;
        }
        data.success ? this.onConfirm() : this.connectionFailure();
      },
      error: (error) => Log.error('Error in added articles subscription: ', error),
    }));

    this.subscriptions.add(this._defaultReplaceArticleManager.replacedArticles$.subscribe({
      next: (succeed) => (succeed ? this.onConfirm() : this.connectionFailure()),
      error: (error) => Log.error('Error in replaced articles subscription: ', error),
    }));

    this.subscriptions.add(this._alternativeReplaceArticleManager.replacedArticles$.subscribe({
      next: (succeed) => (succeed ? this.onConfirm() : this.connectionFailure()),
      error: (error) => Log.error('Error in replaced alternative articles subscription: ', error),
    }));

    this.onClose = new Subject();
  }

  public ngOnDestroy(): void {
    this.addArticlePresenter.removeFromContext();

    this.subscriptions.unsubscribe();
    this.subscriptions = undefined;
  }

  private onActionAccessoriesEditor(actionData: AccessoryEditorAction): void {
    switch (actionData.action) {
      case this.accessoryEditorStateManager.ACTION_OPEN_ACCESSORIES:
        this.onOpen(actionData.searchType);
        break;
      case this.accessoryEditorStateManager.ACTION_CLOSE_ACCESSORIES:
        this.closeAccessory();
        break;
      case this.accessoryEditorStateManager.ACTION_UPDATE_ACCESSORIES:
        this.updateAccessories();
        break;
    }
  }

  /**
   * Recreates the ArticlePresenter.
   * (Bugfix for issues:
   *  SD Bug 43291: Beschriftung setzen führt zum Fehler
   *  SD Bug 43536: Beschriftungsmaterial wird doppelt projektiert
   *  SD Bug 43539: Nach Zubehör setzen löscht Undo Funktion zuviel)
   *
   * Over the course of adding accessories the ArticleCreationManager inside of ArticlePresenter can accumulate objects in _createdArticleContainers.
   * This lead to problems with the Undo-Operations created for the additions.
   *
   * The fix recreates a new ArticlePresenter after each addition of an accessory. This way nothing can accumulate any more.
   * This method is called by _articleConnectedHandler and _articleConnectedFailureHandler, which signal the completion of the adding-accessory workflow.
   */
  private recreateArticlePresenter(): void {
    this.accessoryList = [];
    const previousFilter = this.addArticlePresenter?.accessoryTypeFilter;
    if (this.addArticlePresenter) {
      this.addArticlePresenter.removeFromContext();
      this.addArticlePresenter = null;
    }

    this.articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    this.addArticlePresenter = this.addArticlePresenterFactory.create(this.articleCreationManager, previousFilter);

    if (this.allowedAccessoryTypeKeys) {
      this.accessoryCategories = this.addArticlePresenter.allAccessoryTypeFilters
        .filter((type) => this.allowedAccessoryTypeKeys.includes(type.localizationKey))
        .map((type) => type.localizationKey);
      this.accSelectedRow = this.addArticlePresenter.allAccessoryTypeFilters.find(
        (a) => a.localizationKey === this.allowedAccessoryTypeKeys[0]
      ).localizationKey;
    } else if (this.articleTypeSelectionAllowed) {
      this.accessoryCategories = this.addArticlePresenter.allAccessoryTypeFilters.map((type) => type.localizationKey);
      this.accSelectedRow = this.addArticlePresenter.allAccessoryTypeFilters.find(
        (a) => a.localizationKey === AccessoryTypeFilter.LANGUAGE_KEY_TOP_RESULTS
      ).localizationKey;
    }
  }

  public onConfirm(): void {
    this.sceneManager.renderScene();
    const lastInsertedAccessory: ArticleUsageVO = this.getLastInsertedAccessory();
    this.recreateArticlePresenter();
    this.changeArticleSelection(lastInsertedAccessory);
    this.onClose.next(true);
  }

  // has to be called before recreateArticlePresenter() is called
  private getLastInsertedAccessory(): ArticleUsageVO {
    const lastInsertedAccessory: ArticleUsageVO[] = this.articleCreationManager.getCreatedArticleUsages();
    return lastInsertedAccessory[lastInsertedAccessory.length - 1];
  }

  // has to be called after recreateArticlePresenter() is called
  private changeArticleSelection(lastInsertedAccessory: ArticleUsageVO): void {
    const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(lastInsertedAccessory);
    if (ArticleUtils.isEndPlateArticle(article)) {
      this.selectionModelService.selectArticlesByUsageIds([lastInsertedAccessory.id]);
    }
  }

  protected onOpen(data: string): void {
    this.isOpen = true;
    document.querySelector('body').classList.add('accessory-aside-menu');
    document.querySelector('body').classList.remove('aside-menu-plausi');
    this.labelEditorStateManager.closeAsideLabelEditor();
    this.accessoryEditorStateManager.editorStateChanged(AccessoryEditorState.OPEN);
    this.nothingSelected = ArrayUtils.isEmpty(this.selectionModel.selectedObjects);
    this.notPrintableAvailable = false;
    this.isAlternativeSearch = data !== SearchQueryType.ACCESSORY_ARTICLE;
    this.accessoryList = [];
    this.checkMode();
    this.opened.emit();
  }

  public closeAccessory(): void {
    this.accessoryEditorStateManager.editorStateChanged(AccessoryEditorState.CLOSED);
    this.onClose.next(false);
    this.accessoryList = [];
    document.querySelector('body').classList.remove('accessory-aside-menu');
    this.isOpen = false;
    this.noSearchResults = false;
  }

  private checkMode() {
    if (this.isAlternativeSearch) {
      this.accessoryEditorStateManager.editorModeChanged(AccessoryEditorMode.ALTERNATIVE);
    } else {
      this.accessoryEditorStateManager.editorModeChanged(AccessoryEditorMode.ACCESSORY);
    }
  }

  protected updateAccessories(): void {
    this.noSearchResults = false;
    if (this.isOpen) {
      this.nothingSelected = ArrayUtils.isEmpty(this.selectionModel.selectedObjects);
      this.revalidateFilters();
    }
  }

  public isArticleInCollection(articleId: number): boolean {
    return this.collectionService.isArticleInCollection(articleId);
  }

  public async selectArticleType(type: string): Promise<void> {
    this.noSearchResults = false;
    this.selectedAccessory = null;
    this.accessoryList = [];
    this.accSelectedRow = type;
    this.notPrintableAvailable = this.isAccessoryTypeSelected(ArticleTypeConstants.TYPE_LABELING);
    this.addArticlePresenter.accessoryTypeFilter = this.addArticlePresenter.allAccessoryTypeFilters.find((t) => t.localizationKey === this.accSelectedRow);
    const accessoryList = await lastValueFrom(this.addArticlePresenter.findAccessoryArticles());
    this.accessoryListChanged(accessoryList);
  }

  public performSearch(searchElement: HTMLInputElement): void {
    this.revalidateFilters();
    const searchString = searchElement.value;
    const searchResults: Accessory[] = this.accessoryList.filter((accessory) => accessory.articleNr.includes(searchString));
    if (ArrayUtils.isEmpty(searchResults)) {
      this.noSearchResults = true;
    }
    this.accessoryList = searchResults;
  }

  public cancelSearch() {
    this.revalidateFilters();
  }

  private updateCategoriesLanguage() {
    if (this.allowedAccessoryTypeKeys) {
      this.accessoryCategories = this.addArticlePresenter.allAccessoryTypeFilters
        .filter((type) => this.allowedAccessoryTypeKeys.includes(type.localizationKey))
        .map((type) => type.localizationKey);
    } else if (this.articleTypeSelectionAllowed) {
      this.accessoryCategories = this.addArticlePresenter.allAccessoryTypeFilters.map((type) => type.localizationKey);
    }
  }

  public async connect(item: Accessory): Promise<void> {
    // Prepare for logging (selection is cleared during the following operation).
    const accessoryArticleId = item.article.articleId;
    const selectedArticleIds = this.getUniqueSelectedArticleIds();

    const article = await lastValueFrom(this.articleManager.loadFullArticle(item.article.articleId));

    // Selection is cleared by the connect operation
    const connections: SuitableConnectionsVO = new SuitableConnectionsVO();
    connections.article = article;
    connections.connectionSets = item.suitableConnections;

    await this.connectArticleBySuitableConnections(connections);

    this.wireBridgeService.openWireBridgePopupAfterAddingOpenEndedWire(article);

    // Log accessory addition for all selected articles.
    for (const articleId of selectedArticleIds) {
        this.userActionsLoggingManager.logArticleAccessorySelection(articleId, accessoryArticleId);
    }
  }

  private getUniqueSelectedArticleIds(selectedObjects: any[] = this.selectionModel.selectedObjects): number[] {
    const articleIds = new Set<number>();

    for (const obj of selectedObjects) {
      // Selektion einer Klemme:
      if (obj instanceof SelectionMetaElementUsageIdentity) {
        const articleUsageId = (obj as SelectionMetaElementUsageIdentity).articleUsageId;
        const articleUsage = this.projectModel.getArticleUsage(articleUsageId);
        articleIds.add(articleUsage.articleId);
      }

      // Selektion eines Konnektors:
      if (obj instanceof SelectionArticleGroupIdentity) {
        const articleUsageIds = (obj as SelectionArticleGroupIdentity).articleUsageIds;
        for (const articleUsageId of articleUsageIds) {
          const articleUsage = this.projectModel.getArticleUsage(articleUsageId);
          articleIds.add(articleUsage.articleId);
        }
      }
    }

    return Array.from(articleIds);
  }

  public revalidateFilters(): void {
    this.accessoryList = [];
    const accessoryList = this.addArticlePresenter.revalidateFilters();
    this.accessoryListChanged(accessoryList);
    this.noSearchResults = false;
  }

  private connectionFailure(): void {
    this.recreateArticlePresenter();
  }

  private getOutdatedArticleLabel(item: ArticleWithTechnicalPropertiesDTO): string {
    let label = '';
    if (ArticleUtils.isArticleOutdated(item)) {
      label = ArticleUtils.getOutdatedArticleLabel(this.localeResourceManager, item);
    }
    return label;
  }

  private async connectArticleBySuitableConnections(suitableConnections: SuitableConnectionsVO): Promise<void> {
    if (ArticleUtils.isArticleOutdated(suitableConnections.article)) {
      const answer = await this.alertHelper
        .showAlert(
          'outdatedArticlesAlert.title',
          ArticleUtils.getOutdatedArticleLabelWithContinue(suitableConnections.article, this.localeResourceManager),
          null,
          [AlertButton.YES, AlertButton.NO],
          "error"
        );
      if (answer !== AlertButton.YES) {
        return;
      }
    }
    await this.overlayService.doWithOverlay(
      () => this.addArticlePresenter.prepareArticle(suitableConnections),
      OverlayBackground.color);
  }

  private isAccessoryTypeSelected(accessoryType: string): boolean {
    const includedArticleTypes: string[] = this.addArticlePresenter.allAccessoryTypeFilters.find(
      (t) => t.localizationKey === this.accSelectedRow
    ).includedArticleTypes;
    // if no article types are specified to be included, then everything is shown
    if (ArrayUtils.isEmpty(includedArticleTypes)) {
      return false;
    }
    for (const includedType of includedArticleTypes) {
      if (includedType === accessoryType) {
        return true;
      }
    }
    return false;
  }

  private technicalInfosLoaded(): void {
    this.cdRef.markForCheck();
  }

  public async addArticleToArticleCollection(article: ArticleWithTechnicalPropertiesDTO): Promise<void> {
    const articleCollection = await this.collectionService.addArticleToArticleCollection(article.articleId);
    if (articleCollection) {
      if (articleCollection.type === ArticleCollectionVO.TYPE_FAVORITES) {
        const acessoryList = this.addArticlePresenter.revalidateFilters();
        this.accessoryListChanged(acessoryList);
      }
    }
  }

  public showArticleTechnicalInfo(article: ArticleWithTechnicalPropertiesDTO): void {
    ArticlePreviewUtils.openShopLink(article.articleNo, this.localeResourceManager);
  }

  public handleMissingImage(event: Event) {
    (event.target as HTMLImageElement).src = '/assets/productimages/no-image.png';
  }

  private projectChanged(projectChanged: ProjectChanged): void {
    switch (projectChanged.type) {
      case ProjectChangedType.ProjectCleared:
        this.closeAccessory();
        break;
    }
  }

  private async setDataToConnectArticle(accessoryViewData: AccessoryViewData): Promise<void> {
    this.addArticlePresenter.setDataToConnectArticle(accessoryViewData);
    let accessoryList =
      await lastValueFrom(this.addArticlePresenter.loadFavoriteTypeIds().pipe(mergeMap(() => this.addArticlePresenter.findAccessoryArticles())));
    this.accessoryListChanged(accessoryList);
  }

  private accessoryListChanged(accessoryList: ArticleInfoConnectionSetVO[]) {
    if (ArrayUtils.isNotEmpty(accessoryList)) {
      this.noSearchResults = false;
      this.accessoryList = accessoryList.map(item =>
          new Accessory(
            item.articleWithTechnicalProperties,
            item.articleWithTechnicalProperties.articleNo,
            ArticlePreviewUtils.getPreviewImageByNo(item.articleWithTechnicalProperties.articleNo),
            item.articleWithTechnicalProperties.displayName,
            this.getOutdatedArticleLabel(item.articleWithTechnicalProperties),
            item.articleWithTechnicalProperties.properties[ArticleTechnicalInfoPropertyDef.COLOR]?.value?.toString(),
            item.connectionSets,
            this.userModel.getCustomArticleNumber(item.articleWithTechnicalProperties.articleNo),
          ));
    } else {
      this.noSearchResults = true;
    }
    this.cdRef.markForCheck();
  }

  public separatedDisplayName(accessory: Accessory): [string, string] {
    return ArticleUtils.separatedDisplayName(accessory.description);
  }
}
